import { type FormEvent, type ReactNode, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { Lcd } from "../components/Lcd";
import { ErrorNote } from "../components/Shell";
import { useDeps, useResource } from "../context";
import { PasskeyCancelled } from "../lib/passkey";
import { TopBar } from "./Landing";

/** "Send me a sign-in link": the same email flow on sign-up and log-in. */
function EmailLinkForm({ next, hot }: { next?: string; hot?: boolean }) {
  const { api } = useDeps();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<{ s: "idle" | "sending" | "sent" } | { s: "error"; error: Error }>({
    s: "idle",
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setState({ s: "sending" });
    try {
      await api.sendEmailLink(email.trim(), next);
      setState({ s: "sent" });
    } catch (error) {
      setState({ s: "error", error: error instanceof Error ? error : new Error(String(error)) });
    }
  }

  if (state.s === "sent")
    return (
      <p className="ok-msg" role="status">
        Check your email. We sent a sign-in link to <b>{email.trim()}</b>.
      </p>
    );
  return (
    <form onSubmit={onSubmit} noValidate={false}>
      <label className="label" htmlFor="email">
        Your email
      </label>
      <input
        id="email"
        className="field"
        type="email"
        autoComplete="email"
        required
        placeholder="you@yourapp.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <button type="submit" className={hot ? "btn hot wide" : "btn wide"} disabled={state.s === "sending"}>
        {state.s === "sending" ? "Sending…" : "Send me a sign-in link"}
      </button>
      {state.s === "error" && <ErrorNote error={state.error} />}
    </form>
  );
}

const PROVIDER_LABEL = { google: "Google", github: "GitHub" } as const;

/**
 * "Continue with Google / GitHub", only for providers this server is set up for. While loading or if
 * the list can't be read, nothing: a button that can only fail is worse than none.
 */
function ProviderButtons({ divider }: { divider?: boolean }) {
  const { api, redirect } = useDeps();
  const providers = useResource((a) => a.oauthProviders());
  const list = providers.status === "ok" ? providers.data : [];
  if (list.length === 0) return null;
  return (
    <>
      {list.map((p) => (
        <button key={p} type="button" className="btn alt wide" onClick={() => redirect(api.oauthStartUrl(p))}>
          Continue with {PROVIDER_LABEL[p]}
        </button>
      ))}
      {divider && <div className="or">or</div>}
    </>
  );
}

/** What happens after "Start free", in order. Shown beside the form so sign-up isn't a leap in the dark. */
const SIGNUP_STEPS: { title: string; text: ReactNode }[] = [
  { title: "Open the link we email you", text: "No password. Then save a passkey: Face ID or fingerprint." },
  {
    title: "Connect your app",
    text: (
      <>
        Run <code>npx pagerless connect</code> in your repo, or ask your AI to add Pagerless.
      </>
    ),
  },
  { title: "Pick your backup", text: "One person we call if you don't answer within 5 minutes." },
];

export function Signup() {
  return (
    <>
      <title>Create your account · Pagerless</title>
      <TopBar minimal />
      <main id="main" className="center">
        <div className="signup">
          <div className="win auth">
            <h1 className="sr-only">Create your account</h1>
            <Lcd size="sm" lines={["START FREE"]} />
            <p className="note" style={{ marginTop: 12 }}>
              You + one backup. No card, no password.
            </p>
            <ProviderButtons divider />
            <EmailLinkForm hot />
            <p className="note">
              Already have an account? <Link to="/login">Log in</Link>
            </p>
          </div>
          <section className="signup-next" aria-labelledby="signup-next-h">
            <h2 id="signup-next-h" className="h2">
              Five minutes, then sleep
            </h2>
            <ol>
              {SIGNUP_STEPS.map((s, i) => (
                <li key={s.title}>
                  <span className="num" aria-hidden="true">
                    {i + 1}
                  </span>
                  <div>
                    <b>{s.title}</b>
                    <p>{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <p className="note">
              Free covers you, one backup and 3 apps. Calls go through your own Twilio, never marked up.
            </p>
          </section>
        </div>
      </main>
    </>
  );
}

export function Login() {
  const { api, passkey } = useDeps();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? "/home";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [byEmail, setByEmail] = useState(false);

  async function logIn() {
    setBusy(true);
    setError(null);
    try {
      const options = await api.passkeyLoginOptions();
      const response = await passkey.login(options);
      const r = await api.passkeyLoginVerify(response);
      if (!r.ok) throw new Error("We couldn't log you in with that passkey.");
      navigate(from, { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <title>Log in · Pagerless</title>
      <TopBar minimal />
      <main id="main" className="center">
        <div className="win auth">
          <h1 className="sr-only">Log in</h1>
          <Lcd size="sm" lines={["WELCOME BACK"]} />
          <button type="button" className="btn wide" style={{ marginTop: 16 }} onClick={logIn} disabled={busy}>
            {busy ? "Waiting for Face ID…" : "Log in with Face ID"}
          </button>
          {error &&
            (error instanceof PasskeyCancelled ? (
              <p className="err" role="alert">
                Face ID was cancelled. Try again, or use an email link.
              </p>
            ) : (
              <ErrorNote error={error} />
            ))}
          <ProviderButtons />
          {byEmail ? (
            <EmailLinkForm next={from} />
          ) : (
            <button type="button" className="btn alt wide" onClick={() => setByEmail(true)}>
              Email me a sign-in link
            </button>
          )}
          <p className="note">No passkey on this device? Use an email link. No passwords to forget.</p>
          <p className="note">
            New here? <Link to="/signup">Create an account</Link>
          </p>
        </div>
      </main>
    </>
  );
}

const LINK_TOKEN = /^[A-Za-z0-9_-]{20,100}$/;

/**
 * Where emailed sign-in links land (bugs #10, #16). Opening the page never uses the link (mail
 * scanners prefetch links); "Continue" POSTs the token, then goes where the api says.
 */
export function AuthVerify() {
  const { api } = useDeps();
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token") ?? "";
  const valid = LINK_TOKEN.test(token);
  const [state, setState] = useState<{ s: "idle" | "busy" } | { s: "error"; error: Error }>({ s: "idle" });

  async function go() {
    setState({ s: "busy" });
    try {
      const { next } = await api.verifyEmailLink(token);
      navigate(next, { replace: true });
    } catch (error) {
      setState({ s: "error", error: error instanceof Error ? error : new Error(String(error)) });
    }
  }

  return (
    <>
      <title>Sign in · Pagerless</title>
      <TopBar minimal />
      <main id="main" className="center">
        <div className="win auth">
          <h1 className="sr-only">Sign in to Pagerless</h1>
          <Lcd size="sm" lines={["SIGN IN"]} />
          {valid && state.s !== "error" ? (
            <button
              type="button"
              className="btn wide"
              style={{ marginTop: 16 }}
              onClick={go}
              disabled={state.s === "busy"}
            >
              {state.s === "busy" ? "Signing you in…" : "Continue"}
            </button>
          ) : (
            <>
              <div style={{ marginTop: 16 }}>
                {state.s === "error" ? (
                  <ErrorNote error={state.error} />
                ) : (
                  <p className="err" role="alert">
                    This sign-in link is not valid.
                  </p>
                )}
              </div>
              <p className="note">
                <Link to="/login">Get a new link</Link>
              </p>
            </>
          )}
        </div>
      </main>
    </>
  );
}
